/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useGetPatientVisitQuery, useUpdatePatientVisitMutation } from "store";
import {
    Box,
    Spinner,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Button,
    Text,
    Textarea,
    Grid,
    GridItem,
    Link as ChakraLink,
} from "@chakra-ui/react";

const UpdateVisit = () => {
    const { patientId, visitId } = useParams();
    console.log("Params:", { patientId, visitId });
    const navigate = useNavigate();

    // Fetch the existing visit data
    const { data: visit, error, isLoading } = useGetPatientVisitQuery({
        patientId,
        visitId,
    });

    // Mutation hook for updating the visit
    const [
        updatePatientVisit,
        { isLoading: isUpdating, error: updateError },
    ] = useUpdatePatientVisitMutation();

    // Initialize the form with useForm
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm();

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Populate the form with existing visit data
    useEffect(() => {
        if (visit) {
            reset({
                reason: visit.reason,
                visit_notes: visit.visit_notes,
                diagnosis: visit.diagnosis,
                treatment: visit.treatment,
                heent: visit.heent,
                cns: visit.cns,
                chest: visit.chest,
                cvs: visit.cvs,
                genitalia: visit.genitalia,
                pulse: visit.pulse,
                bp: visit.bp,
                spo2: visit.spo2,
                ext: visit.ext,
                abdomen: visit.abdomen,
                weight: visit.weight,
            });
        }
    }, [visit, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            await updatePatientVisit({ patientId, visitId, ...data }).unwrap();
            navigate(`/patients/${patientId}/visits`);
        } catch (error) {
            console.error("Error updating visit:", error);
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Box textAlign="center" mb={4}>
                            <Text fontSize="2xl" fontWeight="bold">
                                Update Visit
                            </Text>
                        </Box>
                    </CardHeader>

                    <CardBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Reason */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.reason}>
                                        <FormLabel>Reason</FormLabel>
                                        <Textarea
                                            {...register("reason", {
                                                minLength: {
                                                    value: 5,
                                                    message:
                                                        "Reason should be at least 5 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Reason should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "reason",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.reason &&
                                                errors.reason.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Notes */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.visit_notes}>
                                        <FormLabel>Notes</FormLabel>
                                        <Textarea
                                            {...register("visit_notes", {
                                                minLength: {
                                                    value: 5,
                                                    message:
                                                        "Notes should be at least 5 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Notes should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "visit_notes",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.visit_notes &&
                                                errors.visit_notes.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Diagnosis */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.diagnosis}>
                                        <FormLabel>Diagnosis</FormLabel>
                                        <Textarea
                                            {...register("diagnosis", {
                                                minLength: {
                                                    value: 5,
                                                    message:
                                                        "Diagnosis should be at least 5 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Diagnosis should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "diagnosis",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.diagnosis &&
                                                errors.diagnosis.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Treatment */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.treatment}>
                                        <FormLabel>Treatment</FormLabel>
                                        <Textarea
                                            {...register("treatment", {
                                                minLength: {
                                                    value: 5,
                                                    message:
                                                        "Treatment should be at least 5 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Treatment should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "treatment",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.treatment &&
                                                errors.treatment.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Row 1 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormControl>
                                        <FormLabel>HEENT</FormLabel>
                                        <Input
                                            type="text"
                                            {...register("heent")}
                                        />
                                    </FormControl>
                                </Box>

                                <Box>
                                    <FormControl>
                                        <FormLabel>CNS</FormLabel>
                                        <Input
                                            type="text"
                                            {...register("cns")}
                                        />
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Row 2 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormLabel>Chest</FormLabel>
                                    <Input type="text" {...register("chest")} />
                                </Box>

                                <Box>
                                    <FormLabel>CVS</FormLabel>
                                    <Input type="text" {...register("cvs")} />
                                </Box>
                            </SimpleGrid>

                            {/* Row 3 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormLabel>Genitalia</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("genitalia")}
                                    />
                                </Box>

                                <Box>
                                    <FormLabel>Pulse</FormLabel>
                                    <Input type="text" {...register("pulse")} />
                                </Box>
                            </SimpleGrid>

                            {/* Row 4 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormLabel>BP</FormLabel>
                                    <Input type="text" {...register("bp")} />
                                </Box>

                                <Box>
                                    <FormLabel>SPO2</FormLabel>
                                    <Input type="text" {...register("spo2")} />
                                </Box>
                            </SimpleGrid>

                            {/* Row 5 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormLabel>Ext</FormLabel>
                                    <Input type="text" {...register("ext")} />
                                </Box>

                                <Box>
                                    <FormLabel>Abdomen</FormLabel>
                                    <Input
                                        type="text"
                                        {...register("abdomen")}
                                    />
                                </Box>
                            </SimpleGrid>

                            {/* Row 6 */}
                            <SimpleGrid columns={{ base: 1, md: 2 }} mb={6}>
                                <Box>
                                    <FormControl>
                                        <FormLabel>Weight</FormLabel>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            {...register("weight", {
                                                min: {
                                                    value: 0.01,
                                                    message:
                                                        "Weight must be greater than 0",
                                                },
                                                max: {
                                                    value: 1100,
                                                    message:
                                                        "Weight cannot exceed 1100 lbs",
                                                },
                                                valueAsNumber: true,
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.weight &&
                                                errors.weight.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            <Grid
                                templateColumns="repeat(2, 1fr)"
                                gap={4}
                                width="100%"
                            >
                                <GridItem>
                                    <ChakraLink
                                        as={Link}
                                        to={`/patients/${patientId}/visits/${visitId}/details`}
                                        color="blue.500"
                                    >
                                        Back to Details
                                    </ChakraLink>
                                </GridItem>
                                <GridItem textAlign="right">
                                    <Button
                                        colorScheme="blue"
                                        isLoading={isUpdating}
                                        loadingText="Loading..."
                                        spinner={<Spinner />}
                                        type="submit"
                                    >
                                        Update
                                    </Button>
                                </GridItem>
                            </Grid>

                            {updateError && (
                                <div>Error: {updateError.message}</div>
                            )}
                        </form>
                    </CardBody>
                </Card>
            </Box>
        </>
    );
};

export default UpdateVisit;
